/**
 * The discovery slashes — ``/?``, ``/help``, ``/skills``, ``/list``.
 *
 * Deliberately **not** Cosmos skill documents. A skill document is a routable
 * unit of work (system prompt, allowed tools, persona allow-list membership,
 * model tier); these have none of that and must never acquire it. Modelling
 * them as skills would let an admin revoke a user's ability to ask "what can
 * you do?", let the router hand ``/?`` to the LLM, and make them list
 * themselves inside their own listing.
 *
 * They exist here so the ``/`` palette can offer them — without that, the
 * discovery command is itself undiscoverable. Four spellings because users
 * reach for different words for the same question; all four render the same
 * listing.
 *
 * ``skillKey`` is empty and there is no ``autoSendWhen``, which is what makes
 * the flow work: selecting one prefills ``"/? "`` in the composer, and Enter
 * falls through to the local interception in ``useChat.runChatTurn`` rather
 * than starting a chat turn.
 */
import type { SlashCommand } from "./types";

/** Canonical set of discovery slashes, lower-case with the leading sigil. */
export const DISCOVERY_SLASHES: ReadonlySet<string> = new Set(["/?", "/help", "/skills", "/list"]);

const DISCOVERY_DESCRIPTION = "List everything I can do on this channel";

/** Palette entries for the discovery slashes, in the order they should render. */
export const DISCOVERY_COMMANDS: readonly SlashCommand[] = Object.freeze([
  {
    slash: "/?",
    skillKey: "",
    label: "Help",
    description: DISCOVERY_DESCRIPTION,
    hint: "",
    prompt: "",
    category: "discovery",
    autoApply: false,
  },
  {
    slash: "/help",
    skillKey: "",
    label: "Help",
    description: "Same listing as /?",
    hint: "",
    prompt: "",
    category: "discovery",
    autoApply: false,
  },
  {
    slash: "/skills",
    skillKey: "",
    label: "Skills",
    description: "Same listing as /?",
    hint: "",
    prompt: "",
    category: "discovery",
    autoApply: false,
  },
  {
    slash: "/list",
    skillKey: "",
    label: "List",
    description: "Same listing as /?",
    hint: "",
    prompt: "",
    category: "discovery",
    autoApply: false,
  },
]);

/** True when ``slash`` (any case, with or without surrounding space) is a discovery slash. */
export function isDiscoveryCommand(slash: string): boolean {
  return DISCOVERY_SLASHES.has(slash.trim().toLowerCase());
}
